import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, Info } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { api, errMsg, inr } from "@/lib/api";
import { useQuote, WalletBox, Summary } from "@/pages/Cart";

const loadRzp = () => new Promise((res) => {
  if (window.Razorpay) return res(true);
  const s = document.createElement("script"); s.src = "https://checkout.razorpay.com/v1/checkout.js";
  s.onload = () => res(true); s.onerror = () => res(false); document.body.appendChild(s);
});

function getRef(days) {
  try { const r = JSON.parse(localStorage.getItem("bs_ref")); return r && Date.now() - r.at < days * 864e5 ? r.code : null; } catch { return null; }
}

const Sec = ({ n, title, children }) => <div className="card p-5"><p className="font-display mb-4 flex items-center gap-2 text-lg font-semibold"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#6B1D2F] text-xs text-white">{n}</span>{title}</p>{children}</div>;

export default function Checkout() {
  const { user, cart, clearCart, config, refreshMe } = useApp();
  const loc = useLocation();
  const nav = useNavigate();
  const [useWallet, setUseWallet] = useState(!!loc.state?.useWallet);
  const [delivery, setDelivery] = useState("standard");
  const [pay, setPay] = useState("upi");
  const [a, setA] = useState({ name: user?.name || "", phone: "", line1: "", city: user?.city || "", state: "", pincode: "" });
  const [busy, setBusy] = useState(false);
  const [sim, setSim] = useState(null);
  const [q] = useQuote(useWallet, delivery);
  if (user === null) return <Navigate to="/login" state={{ from: "/checkout" }} />;
  if (!cart.length) return <Navigate to="/cart" />;
  const set = (k) => (e) => setA({ ...a, [k]: e.target.value });

  const finish = async (order, body) => {
    const r = await api.post(`/orders/${order.id}/verify`, body);
    clearCart(); refreshMe();
    nav(`/order-success/${order.id}`, { state: { referral: r.data.referral } });
  };

  const place = async () => {
    if (!/^[6-9]\d{9}$/.test(a.phone)) return toast.error("Enter a valid 10-digit mobile number");
    if (!/^\d{6}$/.test(a.pincode)) return toast.error("Enter a valid 6-digit pincode");
    setBusy(true);
    try {
      const ref = getRef(config?.referral?.validity_days || 30);
      const r = await api.post("/orders", { items: cart.map((i) => ({ product_id: i.id, qty: i.qty })), use_wallet: useWallet, delivery_option: delivery, address: a, ref_code: ref, payment_method: pay });
      const { order } = r.data;
      if (r.data.mode === "razorpay" && (await loadRzp())) {
        new window.Razorpay({
          key: r.data.key_id, amount: r.data.amount, currency: "INR", name: "Bhakti Sakhi", description: `Order ${order.order_no}`, order_id: r.data.razorpay_order_id,
          prefill: { name: a.name, contact: a.phone, email: user.email }, theme: { color: "#6B1D2F" },
          handler: (resp) => finish(order, resp).catch((e) => toast.error(errMsg(e))),
          modal: { ondismiss: () => setBusy(false) },
        }).open();
      } else { setSim(order); setBusy(false); }
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };

  return (
    <div className="container-x py-8">
      <h1 className="font-display mb-6 text-3xl font-bold text-[#6B1D2F]">Checkout</h1>
      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <div className="space-y-5">
          <Sec n="1" title="Address">
            <div className="grid gap-3 sm:grid-cols-2">
              {[["name", "Full name"], ["phone", "Mobile number"], ["line1", "House, street, area"], ["city", "City"], ["state", "State"], ["pincode", "Pincode"]].map(([k, l]) => <input key={k} className={`input ${k === "line1" ? "sm:col-span-2" : ""}`} placeholder={l} value={a[k]} onChange={set(k)} data-testid={`address-${k}`} />)}
            </div>
          </Sec>
          <Sec n="2" title="Delivery">
            <div className="grid gap-3 sm:grid-cols-2">
              {[["standard", "Standard", "3–6 days", q?.subtotal >= (config?.delivery?.free_above || 499) ? "FREE" : inr(config?.delivery?.fee)], ["express", "Express", "1–3 days (metros)", `+${inr(config?.delivery?.express_fee)}`]].map(([v, l, s, p]) => (
                <button key={v} onClick={() => setDelivery(v)} data-testid={`delivery-${v}`} className={`rounded-2xl border p-4 text-left ${delivery === v ? "border-[#6B1D2F] bg-[#FDF2F4]" : "border-[#E7E0D3]"}`}><p className="font-semibold">{l} <span className="float-right text-sm">{p}</span></p><p className="text-xs text-[#78716C]">{s}</p></button>
              ))}
            </div>
          </Sec>
          <Sec n="3" title="Payment">
            <div className="grid gap-3 sm:grid-cols-3">
              {[["upi", "UPI"], ["card", "Card"], ["netbanking", "Netbanking"]].map(([v, l]) => <button key={v} onClick={() => setPay(v)} data-testid={`pay-${v}`} className={`rounded-2xl border p-3 text-sm font-semibold ${pay === v ? "border-[#6B1D2F] bg-[#FDF2F4]" : "border-[#E7E0D3]"}`}>{l}</button>)}
            </div>
            <p className="mt-3 flex items-center gap-1.5 text-xs text-[#78716C]"><ShieldCheck className="h-4 w-4 text-[#15803D]" />Secured by Razorpay{config?.payment_mode === "simulated" && " · TEST MODE (simulated payment until Razorpay keys are added)"}</p>
          </Sec>
          <Sec n="4" title="Wallet Rewards">{q && <WalletBox q={q} useWallet={useWallet} setUseWallet={setUseWallet} />}</Sec>
        </div>
        <div className="space-y-4 lg:sticky lg:top-24 lg:self-start">
          {q && <Summary q={q}>
            <div className="rounded-xl bg-[#FBF7EE] p-3 text-xs"><div className="flex justify-between"><span>Order Total</span><b>{inr(q.subtotal + q.delivery)}</b></div><div className="flex justify-between"><span>Wallet Used</span><b>−{inr(q.wallet_used)}</b></div><div className="flex justify-between text-sm text-[#6B1D2F]"><span>Final Payable</span><b>{inr(q.payable)}</b></div></div>
            <button disabled={busy} onClick={place} className="btn-maroon mt-2 w-full !py-3" data-testid="checkout-razorpay-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Pay ${inr(q.payable)}`}</button>
            <p className="flex gap-1 text-[11px] text-[#78716C]"><Info className="mt-0.5 h-3 w-3 shrink-0" />By placing this order you agree to our <Link to="/page/terms" className="underline">Terms</Link> & <Link to="/page/refund" className="underline">Refund Policy</Link>.</p>
          </Summary>}
        </div>
      </div>
      <Dialog open={!!sim} onOpenChange={(o) => !o && setSim(null)}>
        <DialogContent className="max-w-sm rounded-3xl bg-[#FFFDF7]" data-testid="simulated-payment-dialog">
          <DialogTitle className="font-display text-xl text-[#6B1D2F]">Test Payment</DialogTitle>
          <DialogDescription>Razorpay keys are not configured yet, so this is a simulated payment for order {sim?.order_no}.</DialogDescription>
          <p className="font-display text-3xl font-bold">{inr(sim?.payable)}</p>
          <button className="btn-maroon w-full" data-testid="simulated-pay-btn" onClick={() => finish(sim, { simulated: true }).catch((e) => toast.error(errMsg(e)))}>Simulate successful payment</button>
          <button className="btn-ghost w-full" onClick={() => setSim(null)}>Cancel</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
